import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AuthEntryCard } from "@/features/auth/components/AuthEntryCard";
import { Button } from "@/shared/components/ui/Button";
import { passwordResetService } from "@/features/auth/services/passwordReset.service";
import {
  getPasswordResetState,
  setPasswordResetStarted,
} from "@/features/auth/store/passwordReset.store";

const OTP_LENGTH = 6;
const MAX_OTP_ATTEMPTS = 3;

function formatCountdown(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remaining).padStart(2, "0")}`;
}

export function ForgotPasswordCodePage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("auth");
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const { correlationId, maskedEmail, email, expiresInSeconds } =
    getPasswordResetState();

  const [otpDigits, setOtpDigits] = useState<string[]>(
    Array(OTP_LENGTH).fill(""),
  );
  const [secondsLeft, setSecondsLeft] = useState(expiresInSeconds);
  const [showOtpError, setShowOtpError] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [isCodeExpired, setIsCodeExpired] = useState(false);

  const otpValue = otpDigits.join("");
  const canVerify = otpValue.length === OTP_LENGTH && !isVerifying;

  useEffect(() => {
    if (!correlationId) {
      navigate("/forgot-password", { replace: true });
    }
  }, [correlationId, navigate]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = window.setTimeout(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  function updateDigit(index: number, nextValue: string) {
    const sanitized = nextValue.replace(/\D/g, "").slice(-1);
    setShowOtpError(false);

    setOtpDigits((current) => {
      const next = [...current];
      next[index] = sanitized;
      return next;
    });

    if (sanitized && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, key: string) {
    if (key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (key === "Enter" && canVerify) {
      void handleVerify();
    }
  }

  function handlePaste(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
    if (!digits.length) return;

    setShowOtpError(false);
    setOtpDigits((current) => {
      const next = [...current];
      digits.forEach((digit, index) => {
        next[index] = digit;
      });
      return next;
    });

    const nextFocusIndex = Math.min(digits.length, OTP_LENGTH - 1);
    inputRefs.current[nextFocusIndex]?.focus();
  }

  async function handleResend() {
    if (!email || isResending) return;

    setIsResending(true);
    const result = await passwordResetService.start({
      email,
      lang: i18n.language,
    });
    setIsResending(false);

    if (result.success) {
      setPasswordResetStarted(
        result.data.correlationId,
        result.data.maskedEmail,
        result.data.expiresInSeconds,
        email,
      );
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      setSecondsLeft(result.data.expiresInSeconds);
      setShowOtpError(false);
      setFailedAttempts(0);
      inputRefs.current[0]?.focus();
    }
  }

  async function handleVerify() {
    if (!canVerify || !correlationId) return;

    setIsVerifying(true);
    setShowOtpError(false);

    const result = await passwordResetService.verify(correlationId, {
      code: otpValue,
    });

    setIsVerifying(false);

    if (result.success) {
      navigate("/forgot-password/password", { replace: true });
      return;
    }

    // Código expirado: el servidor devuelve ERR_PASSWORD_RESET_CODE_EXPIRED (HTTP 423)
    if (result.errorCode === "ERR_PASSWORD_RESET_CODE_EXPIRED") {
      setIsCodeExpired(true);
      setSecondsLeft(0);
      return;
    }

    const nextFailed = failedAttempts + 1;
    setFailedAttempts(nextFailed);
    setShowOtpError(true);

    if (nextFailed >= MAX_OTP_ATTEMPTS) {
      setShowBlockedModal(true);
    }
  }

  const timerLabel = useMemo(
    () =>
      t("auth:forgotPassword.code.timer", {
        time: formatCountdown(secondsLeft),
      }),
    [secondsLeft, t],
  );

  return (
    <AuthEntryCard variant="otp-code">
      <button
        aria-label={t("auth:forgotPassword.code.close")}
        className="absolute right-0 top-0 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-soft bg-surface-card text-xl leading-none text-text-muted transition-colors duration-150 hover:border-brand-primary hover:text-brand-primary"
        onClick={() => navigate("/forgot-password")}
        type="button"
      >
        ×
      </button>

      <section className="mt-4 w-full max-w-96 text-center">
        <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">
          {t("auth:forgotPassword.code.title")}
        </h1>
        <p className="mt-3 text-sm font-semibold leading-5 text-brand-primary">
          {timerLabel}
        </p>
        <p className="mt-3 text-sm leading-6 text-text-muted">
          {t("auth:forgotPassword.code.description", {
            maskedEmail: maskedEmail ?? "",
          })}
        </p>
      </section>

      <div className="mt-8 w-full max-w-96">
        <p className="mb-4 text-center text-[0.8125rem] leading-6 text-text-muted">
          {t("auth:forgotPassword.code.label")}
        </p>

        <div className="flex justify-center gap-2 sm:gap-3">
          {otpDigits.map((digit, index) => (
            <input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              aria-label={t("auth:forgotPassword.code.digit.label", {
                number: index + 1,
              })}
              className="h-12 w-10 rounded-md border border-border-soft bg-surface-card text-center text-lg font-semibold text-text-primary transition-[border-color,box-shadow] duration-150 focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring sm:w-12"
              data-invalid={showOtpError}
              inputMode="numeric"
              maxLength={1}
              onChange={(event) => updateDigit(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event.key)}
              onPaste={(event) => {
                event.preventDefault();
                handlePaste(event.clipboardData.getData("text"));
              }}
              value={digit}
            />
          ))}
        </div>
      </div>

      <div className="mt-8 flex w-full max-w-96 justify-center">
        <Button
          className="min-h-9 px-5 py-1.5 text-sm disabled:bg-[#f1f1f3] disabled:text-[#8c939b] disabled:opacity-100"
          disabled={!canVerify}
          onClick={() => void handleVerify()}
        >
          {isVerifying
            ? t("auth:forgotPassword.code.btn.verifying")
            : t("auth:forgotPassword.code.btn.verify")}
        </Button>
      </div>

      <p className="mt-6 text-center text-sm leading-6 text-text-muted">
        {t("auth:forgotPassword.code.resend.question")}{" "}
        {secondsLeft > 0 ? (
          <span>
            {t("auth:forgotPassword.code.resend.countdown", {
              time: formatCountdown(secondsLeft),
            })}
          </span>
        ) : (
          <button
            className="border-none bg-transparent p-0 text-sm font-medium text-brand-primary underline-offset-2 hover:underline disabled:text-text-muted disabled:no-underline"
            disabled={isResending}
            onClick={() => void handleResend()}
            type="button"
          >
            {isResending
              ? t("auth:forgotPassword.code.resend.sending")
              : t("auth:forgotPassword.code.resend.ready")}
          </button>
        )}
      </p>

      {secondsLeft <= 0 || isCodeExpired ? (
        <p
          className="mt-4 text-center text-sm leading-6 text-[#e02d3c]"
          role="alert"
        >
          {t("auth:forgotPassword.code.error.expired")}
        </p>
      ) : showOtpError ? (
        <p
          className="mt-4 text-center text-sm leading-6 text-[#e02d3c]"
          role="alert"
        >
          {t("auth:forgotPassword.code.error.invalid")}{" "}
          {failedAttempts < MAX_OTP_ATTEMPTS ? (
            <span>
              {t("auth:forgotPassword.code.error.attemptsLeft", {
                count: MAX_OTP_ATTEMPTS - failedAttempts,
              })}
            </span>
          ) : null}
        </p>
      ) : null}

      {showBlockedModal ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ffebee] text-2xl font-bold text-[#ff384d]"
              aria-hidden="true"
            >
              !
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">
              {t("auth:forgotPassword.code.blocked.title")}
            </h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {t("auth:forgotPassword.code.blocked.description")}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 bg-[#ff384d] px-5 py-1.5 text-sm hover:bg-[#e03245] active:bg-[#c92d3e]"
                onClick={() => navigate("/forgot-password")}
              >
                {t("auth:forgotPassword.code.blocked.btn")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </AuthEntryCard>
  );
}
